<template>
  <task-form
    :errors="errors"
    :form="form"
    class="fieldset"
    allow-media
    :institutions="institutions"
    :topics="topics"
  >
    <task-editor v-if="page.props.features.visualEditorForTasks" v-model="form" />

    <s-form-list
      v-model="blockPairs"
      formTitle="Create DataDrivenTask"
      title="Blocks"
      addLabel="Add Block "
      saveLabel="Save Block"
      :menuOptions="blockTypeMenu"
      :displayValue="(item?: any) => getBlockName(item?.uiConfig || {})"
      :item-key="(item) => item.uiConfig.uuid"
      removeLabel="Remove Task Block"
      placeholder="No Blocks Added"
      sortable
      removable
      confirmRemove
    >
      <template #item="{item, index: i}">
        <div class="flex-1 min-w-0 flex w-full">
          <button
            class="group flex-1 min-w-0 flex items-center justify-between gap-1.5 w-full text-left px-2.5 first:pl-3 py-2.5 group-first:pt-3 group-last:pb-3 hover:text-blue-600 hover:bg-gray-200/20"
            @click.prevent="selectedBlockPair = item"
          >
            <s-icon
              v-if="item.hasErrors"
              name="warning"
              size="16"
              class="text-red-600 opacity-80 flex-none"
            />
            <s-icon :name="getBlockIcon(item.uiConfig)" size="16" />
            <span class="flex-1 min-w-0 truncate">
              {{ getBlockName(item.uiConfig) }}
            </span>
            <s-icon
              name="pencil"
              size="20"
              class="text-blue-500 opacity-0 transition group-hover:opacity-50"
            />
          </button>
          <button
            type="button"
            title="duplicate"
            class="border-l border-gray-200 text-blue-300 hover:text-blue-500 flex-none flex items-center justify-center py-2.5 px-3 opacity-70 hover:opacity-100 transition-all ease-out duration-150 bg-transparent hover:bg-gray-200/20"
            @click.prevent="duplicate(item)"
          >
            <s-icon name="content-duplicate" size="18" />
          </button>
        </div>
      </template>
    </s-form-list>

    <feedback-instructions-builder
      v-if="hasAiBlocks && form.gradingAlgorithm.feedbackInstructions"
      v-model="form.gradingAlgorithm.feedbackInstructions"
      :errors="errors?.gradingAlgorithm?.feedbackInstructions"
    />

    <task-variable-form
      v-model:variables="form.variables as AnyTaskVariableDto[]"
      :errors="errors?.variables"
    />
  </task-form>

  <block-pair-form-modal
    v-if="selectedBlockPair"
    :form
    :task-id="form.id as number"
    :files="form.files || []"
    :variables="form.variables as AnyTaskVariableDto[]"
    :errors="selectedBlockPair.errors"
    :ui-config="selectedBlockPair.uiConfig"
    @update:ui-config="form.uiConfigs[selectedBlockPair.uiConfigIndex] = $event"
    :component-grader="selectedBlockPair.componentGrader"
    @update:component-grader="updateComponentGrader($event)"
    open
    @close="selectedBlockPair = null"
  />
</template>

<script lang="ts" setup>
import SFormList from '../../../design-system/SFormList.vue';
import {v4 as uuidv4} from 'uuid';
import TaskVariableForm from './Variables/TaskVariableForm.vue';
import {computed, ref, watch} from 'vue';
import {useValidationErrors} from '../../../util/validation-errors';
import BlockPairFormModal from './BlockPairFormModal.vue';
import {useI18n} from 'vue-i18n';
import SIcon from '../../../design-system/SIcon.vue';
import TaskForm from '../../../forms/TaskForm.vue';
import {useForm, usePage} from '@inertiajs/vue3';
import TaskEditor from '../../../editors/task-editor/TaskEditor.vue';
import {Institution} from '../../../types/entities/institution';
import {useBlockTypeLibrary} from '../../../blocks/useBlockTypeLibrary';
import {BlockType} from '../../../blocks/block';
import DataDrivenTaskFormDto = App.DTOs.Tasks.DataDrivenTaskFormDto;
import TaskComponentGrader = App.Entities.Tasks.ComponentGraders.TaskComponentGrader;
import Block = App.Entities.Tasks.Ui.Block;
import {getBlockName} from '../../../blocks/block-name';
import {getBlockIcon} from '../../../blocks/block-icon';
import FeedbackInstructionsBuilder from '../../../components/tasks/ai-parts/FeedbackInstructionsBuilder.vue';
import FeedbackLanguage = App.Enums.PromptBuildingPhrases.FeedbackLanguage;
import FeedbackInstructionsDto = App.DTOs.Tasks.FeedbackInstructionsDto;
import {AnyTaskVariableDto} from '../../../types/entities/tasks';

const page: any = usePage();

const props = defineProps<{
  modelValue?: DataDrivenTaskFormDto;
  isCopy?: boolean;
  errors?: Record<string, any>;
  institutions: Institution[];
  topics: TaskTopicDtoWithLineage[];
}>();

const {errors} = useValidationErrors(props, 'errors');

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const hasAiBlocks = computed(() => {
  return form.uiConfigs.some((block) => {
    return block.type === 'AiGrader';
  });
});

const DEFAULT_FEEDBACK_INSTRUCTIONS: FeedbackInstructionsDto = {
  subject: '',
  provideMistakeLabels: true,
  tone: 'Authoritative',
  formality: 'Formal',
  gradeTuning: null,
  improvementStrategy: null,
  gradeExplanation: null,
  answerRevealStrategy: 'Formative feedback',
  feedbackLength: 'Concise writing',
  useCustomBasePrompt: false,
  maxPastContextLength: 2,
  feedbackLanguagePhrases: ['No empty compliments'] as FeedbackLanguage[],
  customPhrases: [],
};

const form = useForm<DataDrivenTaskFormDto>(
  () =>
    props.modelValue || {
      type: 'DataDrivenTask',
      title: '',
      textbookLink: null,
      textbookLinkText: null,
      textbookLinkIcon: null,
      youtubeId: null,
      status: 'draft',
      draftable: true,
      uiConfigs: [],
      gradingAlgorithm: {
        uuid: uuidv4(),
        componentGraders: [],
        feedbackInstructions: DEFAULT_FEEDBACK_INSTRUCTIONS,
      },
      variables: [],
      files: [],
      institutionId: null,
      topicIds: [],
    }
);

watch(
  () => [form.gradingAlgorithm.feedbackInstructions, hasAiBlocks.value],
  ([newInstructions, hasAiBlocks]) => {
    if (newInstructions === null && hasAiBlocks) {
      form.gradingAlgorithm.feedbackInstructions = DEFAULT_FEEDBACK_INSTRUCTIONS;
    } else if (!hasAiBlocks) {
      form.gradingAlgorithm.feedbackInstructions = null;
    }
  },
  {immediate: true}
);

type BlockPair = {
  componentGraderIndex: number | null;
  componentGrader: TaskComponentGrader | null;
  uiConfigIndex: number;
  uiConfig: Block;
  hasErrors: boolean;
  errors: {
    uiConfig: Record<string, any> | null;
    componentGrader: Record<string, any> | null;
  };
};
const blockPairs = computed<BlockPair[]>({
  get: () => {
    return form.uiConfigs.map((uiConfig, uiConfigIndex) => {
      let componentGraderIndex: number | null = form.gradingAlgorithm.componentGraders.findIndex(
        (grader) => grader.uiComponentUuid === uiConfig.uuid
      );

      let componentGrader: TaskComponentGrader | null = null;
      let componentGraderErrors: Record<string, any> | null = null;
      if (componentGraderIndex !== -1) {
        componentGrader = form.gradingAlgorithm.componentGraders[componentGraderIndex];
        componentGraderErrors =
          errors.value?.gradingAlgorithm?.componentGraders?.[componentGraderIndex];
      } else {
        componentGraderIndex = null;
      }

      const uiConfigErrors = errors.value?.uiConfigs?.[uiConfigIndex];

      const hasErrors = uiConfigErrors || componentGraderErrors;

      return {
        componentGraderIndex,
        componentGrader,
        uiConfigIndex,
        uiConfig,
        hasErrors,
        errors: {
          uiConfig: uiConfigErrors,
          componentGrader: componentGraderErrors,
        },
      };
    });
  },
  set: (blockPairs) => {
    form.uiConfigs = blockPairs.map(({uiConfig}) => uiConfig);

    form.gradingAlgorithm.componentGraders = blockPairs
      .map(({componentGrader}) => componentGrader)
      .filter((item): item is TaskComponentGrader => !!item);
  },
});

/*
 * Component type factories, labels, icons, etc.
 */
const {blockTypes} = useBlockTypeLibrary();

const blockTypeMenu = computed(() =>
  blockTypes.map((blockType) => ({
    label: t('blockTypes.' + blockType.translationKey),
    icon: blockType.icon,
    onClick: () => addBlock(blockType),
  }))
);

const getNewBlockName = ({name, type}: Block): string => {
  const existingNames = new Set(form.uiConfigs.map(({name}) => name));

  let initialGuessIndex = form.uiConfigs.filter((item) => item.type == type).length + 1;
  let newName = '';

  do {
    newName = name + ' ' + initialGuessIndex;
    if (!existingNames.has(newName)) {
      return newName;
    }
    initialGuessIndex++;
  } while (existingNames.has(newName));

  return name;
};

const addBlock = (blockType: BlockType<Block, TaskComponentGrader | null>) => {
  const uiConfig = blockType.uiConfigFactory();

  // Push new components
  uiConfig.name = getNewBlockName(uiConfig);
  form.uiConfigs.push(uiConfig);

  if (blockType.componentGraderFactory) {
    const componentGrader = blockType.componentGraderFactory(uiConfig.uuid);
    componentGrader.name = `${uiConfig.name} Grader`;
    form.gradingAlgorithm.componentGraders.push(componentGrader);
  }
};

const selectedBlockPair = ref<BlockPair | null>(null);

function duplicate(blockPair: BlockPair) {
  const block: Block = {
    ...blockPair.uiConfig,
    uuid: uuidv4(),
    name: blockPair.uiConfig.name + ' (Copy)',
  };

  form.uiConfigs.push(block);

  if (blockPair.componentGrader) {
    form.gradingAlgorithm.componentGraders.push({
      ...blockPair.componentGrader,
      uuid: uuidv4(),
      uiComponentUuid: block.uuid,
    });
  }
}

function updateComponentGrader(graderConfig: any) {
  const index = selectedBlockPair.value?.componentGraderIndex;

  if (typeof index !== 'number' || index < 0) {
    return;
  }

  form.gradingAlgorithm.componentGraders[index] = graderConfig;
}
</script>
